import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import clsx from 'clsx';
import cssStyles from './styles.module.css';
import { popperModifiers, tooltipPlacement } from '../utils';
import { useFocusOnContext } from '../_internal/hooks';
import { Portal } from '../portal';
/**
 * Компонент `<Tooltip/>` предоставляет отображение дополнительной информации о дочернем элементе.
 *
 * @example ```jsx
 *  <Tooltip title="your awesome text">
 *    <RegularButton variant="primary" size="m">
 *      hover me
 *    </Button>
 *  </Tooltip>
 * ```
 */
export const Tooltip = ({
  className,
  title,
  placement = 'top',
  open = null,
  children
}) => {
  const childElement = useRef(null);
  const tooltip = useRef(null);
  const arrow = useRef(null);
  const [openState, setOpenState] = useState(false);
  const isOpen = useMemo(() => open != null ? open : openState, [open, openState]);
  const focusOnContext = !!useFocusOnContext();
  const {
    styles,
    attributes,
    update
  } = usePopper(childElement.current, tooltip.current, {
    placement,
    strategy: focusOnContext ? 'fixed' : 'absolute',
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrow.current,
        padding: 8
      }
    }, ...popperModifiers({
      fallback: tooltipPlacement,
      listeners: isOpen
    })]
  });
  useEffect(() => {
    update == null ? void 0 : update();
  }, [update, children]);
  const wrapperClassName = useMemo(() => clsx(cssStyles.wrapper, className), [className]);
  const isDisabled = useMemo(() => !(open != null ? open : openState), [openState, open]);
  const tooltipClassName = useMemo(() => clsx(cssStyles.tooltip, {
    [cssStyles.disabled]: isDisabled,
    [cssStyles.inModal]: focusOnContext
  }), [isDisabled, focusOnContext]);
  const Wrapper = focusOnContext ? Portal : React.Fragment;
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClassName,
    onMouseEnter: () => setOpenState(true),
    onMouseLeave: () => setOpenState(false),
    onFocus: () => setOpenState(true),
    onBlur: () => setOpenState(false)
  }, /*#__PURE__*/React.cloneElement(children, {
    ref: childElement
  }), /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement("div", {
    className: tooltipClassName,
    ref: tooltip,
    style: styles.popper,
    role: "tooltip",
    ...attributes.popper
  }, /*#__PURE__*/React.createElement("span", {
    className: cssStyles.text
  }, title), /*#__PURE__*/React.createElement("div", {
    className: cssStyles.arrow,
    ref: arrow,
    style: styles.arrow
  }))));
};